import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, Clock } from 'lucide-react';
import { submitContact, ContactForm } from '../../lib/api';
import toast from 'react-hot-toast';
import styles from './Contact.module.css';
import { trackFormSubmission, trackWhatsAppClick, trackEmailClick } from '../../utils/analytics';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  company: '',
  message: ''
};

export default function Contact() {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  
  // Gera um ID único para esta instância do componente
  const instanceId = React.useId();

  const linkedinRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let scriptLoaded = false;
    if (linkedinRef.current) {
      observer = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !scriptLoaded) {
          scriptLoaded = true;
          const script = document.createElement('script');
          script.src = 'https://platform.linkedin.com/badges/js/profile.js';
          script.async = true;
          script.defer = true;
          document.body.appendChild(script);
          observer && observer.disconnect();
        }
      }, { threshold: 0.1 });
      observer.observe(linkedinRef.current);
    }
    return () => {
      observer && observer.disconnect();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await submitContact(formData);
      toast.success('Mensagem enviada com sucesso!');
      trackFormSubmission('contact');
      setFormData(initialFormData);
    } catch {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className={`${styles.contactSection} py-20 bg-white`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Entre em Contato</h2>
          <p className="text-lg text-gray-600">
            Estamos prontos para ajudar a otimizar sua operação logística
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className={`space-y-8 ${styles.contactInfo}`}>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-6">Informações de Contato</h3>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="https://wa.me/5511964772277?text=Gostaria%20de%20saber%20mais%20sobre%20os%20serviços%20da%20Tavares%20Consulting" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => trackWhatsAppClick('contact_page')}
                    >
                      (11) 96477-2277
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Mail className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@danielmtavares.com.br"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => trackEmailClick('contact_page')}
                    >
                      contato@danielmtavares.com.br
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium">Horário de Atendimento</p>
                    <p className="text-gray-600">Segunda a Sexta: 9h às 18h</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg" ref={linkedinRef}>
              <div className="LI-profile-badge" data-version="v1" data-size="medium" data-locale="pt_BR" data-type="horizontal" data-theme="light" data-vanity="danieltavares">
                <a className="LI-simple-link" href="https://www.linkedin.com/in/danieltavares/?trk=public-profile-badge-profile-badge-view-profile-cta">Daniel Tavares</a>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`space-y-6 ${styles.contactForm}`}>
            <div>
              <label htmlFor={`name-${instanceId}`} className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                type="text"
                id={`name-${instanceId}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Seu nome completo"
              />
            </div>
            <div>
              <label htmlFor={`email-${instanceId}`} className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id={`email-${instanceId}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="seu.email@exemplo.com"
              />
            </div>
            <div>
              <label htmlFor={`phone-${instanceId}`} className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                type="tel"
                id={`phone-${instanceId}`}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="(00) 00000-0000"
              />
            </div>
            <div>
              <label htmlFor={`company-${instanceId}`} className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <input
                type="text"
                id={`company-${instanceId}`}
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nome da sua empresa"
              />
            </div>
            <div>
              <label htmlFor={`message-${instanceId}`} className="block text-sm font-medium text-gray-700 mb-1">
                Mensagem
              </label>
              <textarea
                id={`message-${instanceId}`}
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Como podemos ajudar?"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Enviando...' : 'Solicitar Diagnóstico Gratuito'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}